import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { gql, useLazyQuery } from '@apollo/client';
import { Icon } from "@iconify/react/dist/iconify.js";
import leftIcon from '@iconify/icons-solar/arrow-left-linear';
import menu from '@iconify/icons-solar/hamburger-menu-broken';
import close from '@iconify/icons-solar/close-circle-line-duotone';

const GET_GROUP = gql`
    query GetGroup($groupId: String!) {
        # Use schoolGroupById which returns the SchoolGroup interface so
        # the inline fragment on SchoolClass is valid at runtime.
        schoolGroupById(id: $groupId) {
            id
            name
            __typename
            ... on SchoolClass {
                senior
            }
        }
    }
`;

export default function ClassHeader({ selectedClass }) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [isSenior, setIsSenior] = useState(false);
    const [isSchoolClass, setIsSchoolClass] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

    const classId = selectedClass || searchParams.get('class');

    // Use a lazy query without preset variables; execute it only when classId is available.
    const [getGroup, { data, loading, error }] = useLazyQuery(GET_GROUP, {
        fetchPolicy: 'network-only',
    });

    // Execute the query only when we have a valid classId. Pass variables at call time.
    useEffect(() => {
        if (!classId) return;
        getGroup({ variables: { groupId: classId } });
    }, [classId, getGroup]);

    useEffect(() => {
        if (error) {
            console.error('Failed to load group:', error);
            setIsSchoolClass(false);
            setIsSenior(false);
            return;
        }

        if (data && data.schoolGroupById) {
            const grp = data.schoolGroupById;
            const isClass = grp.__typename === 'SchoolClass';
            setIsSchoolClass(isClass);
            setIsSenior(isClass && grp.senior);
        } else {
            setIsSchoolClass(false);
            setIsSenior(false);
        }
    }, [data, loading, error]);

    useEffect(() => {
        const handleResize = () => {
            const small = window.innerWidth < 768;
            if (!small) {
                setIsMenuOpen(false);
            }
            setIsSmallScreen(small);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Preserve class parameter and other query parameters when navigating
    const preserveParams = (path) => {
        const params = new URLSearchParams(searchParams);
        if (selectedClass) {
            params.set('class', selectedClass);
        }
        return `${path}${params.toString() ? `?${params.toString()}` : ''}`;
    };

    // Check if we're in teacher or admin section
    const isTeacher = location.pathname.startsWith('/teacher');
    const isAdmin = location.pathname.startsWith('/admin');

    const prevUrl = isTeacher ? '/teacher' : isAdmin ? '/admin' : '/';

    const navLinks = (
        <ul className={`flex gap-8 text-md ${isSmallScreen ? 'flex-col' : 'flex-row justify-end'}`}>
            <li className="text-gray-700 hover:text-gray-900">
                <Link to={preserveParams('/teacher/')} className={location.pathname === '/teacher/' || location.pathname === '/teacher' ? 'font-bold' : ''}>
                    Dashboard
                </Link>
            </li>
            <li className="text-gray-700 hover:text-gray-900">
                <Link to={preserveParams('/teacher/today')} className={location.pathname === '/teacher/today' ? 'font-bold' : ''}>
                    Tagesübersicht
                </Link>
            </li>
            {/* show stats if senior or if it's a non-class group */}
            {(isSenior || !isSchoolClass) && (
                <li className="text-gray-700 hover:text-gray-900">
                    <Link to={preserveParams('/teacher/stats')} className={location.pathname === '/teacher/stats' ? 'font-bold' : ''}>
                        Statistiken
                    </Link>
                </li>
            )}
            <li className="text-gray-700 hover:text-gray-900">
                <Link to={preserveParams('/teacher/students')} className={location.pathname === '/teacher/students' ? 'font-bold' : ''}>
                    Schülerübersicht
                </Link>
            </li>
            {isSchoolClass && (
                <li className="text-gray-700 hover:text-gray-900">
                    <Link to={preserveParams('/teacher/editclass')} className={location.pathname === '/teacher/editclass' ? 'font-bold' : ''}>
                        Klasseneinstellungen
                    </Link>
                </li>
            )}
        </ul>
    );

    return (
        <nav className="bg-neutral-200 classHeader w-full">
            <div className="container mx-auto px-4 xlg:px-0 py-4 flex justify-end items-center">

                {isSmallScreen ? (
                    <button onClick={() => setIsMenuOpen(true)} className="text-gray-700 rounded-full hover:bg-neutral-300 p-2">
                        <Icon icon={menu} width={28} height={28} />
                    </button>
                ) : (
                    navLinks
                )}
            </div>

            {isSmallScreen && isMenuOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsMenuOpen(false)}></div>
            )}

            <div className={`fixed top-0 right-0 h-full bg-white shadow-lg z-50 p-8 transition-transform transition-opacity transform ${isMenuOpen ? 'translate-x-0 opacity-100 pointer-events-auto' : 'translate-x-full opacity-0 pointer-events-none'} w-64`}>
                <button onClick={() => setIsMenuOpen(false)} className="absolute top-4 right-4 text-gray-700 rounded-full hover:bg-gray-100 ">
                    <Icon icon={close} width={32} height={32} />
                </button>
                <div className="mt-8">
                    {navLinks}
                </div>
            </div>
        </nav>
    );
}